import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClient } from "urql";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormHelperText,
  TextField,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';
import FullScreenLoadCircle from "../FullScreenLoadCircle";
import { DetectionSignalTypes, SignalThresholdLevels, SignalNoticeTypes } from "applications/signal/constants";
import { useSaveSignalSetting } from 'applications/signal/hooks/gqlHooks';
import { useGroupCategoriesBrandsStore } from 'applications/signal/stores/useGroupCategoriesBrandsStore';
import { categoryNameConcat } from 'applications/signal/utils/dataUtils';
import { setGlobalMessage } from 'hooks/useGlobalMessage';
import { DetectionSettingFormData, DetectionSettingFormSchema } from './schema';

/**
 * 更新処理の場合に使用する型
 */
type DetectionSettingProps = {
  id?: string;
  name?: string;
  signal_type: number;
  active?: boolean;
  categories: string[];
  threshold_level: number;
};
const DEFAULT_SIGNAL_TYPE = DetectionSignalTypes.PlummetingItemQuantity.ordinal;
const DETECTION_TARGET_DAYS = 7; // 検知系は固定で7日に設定

export default function DetectionSettingEditForm({ registeredFormData }: { registeredFormData?: DetectionSettingProps }) {
  const urqlClient = useClient();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { saveSignalSetting } = useSaveSignalSetting();

  const { data: categoryBrandData, loading: categoryBrandFetching, init: categoryBrandInit } = useGroupCategoriesBrandsStore();
  useEffect(() => { categoryBrandInit(urqlClient); }, [urqlClient]);
  const categoryData = Array.from(new Set(categoryBrandData?.map(row => categoryNameConcat(row)) || [])).sort();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<DetectionSettingFormData>({
    resolver: zodResolver(DetectionSettingFormSchema),
    defaultValues: {
      signalType: registeredFormData?.signal_type || 0,
      category: registeredFormData?.categories[0] || "",
      thresholdLevel: registeredFormData?.threshold_level || SignalThresholdLevels[0].id,
      name: registeredFormData?.name || "",
    },
  });

  const signalTypeOptions = useMemo(() => {
    return Object.values(DetectionSignalTypes).map(type => ({
      label: type.label,
      id: type.ordinal,
    }));
  }, []);

  const onSubmit = async (data: DetectionSettingFormData) => {
    setIsLoading(true);
    const { data: result, error } = await saveSignalSetting({data: {
      id: registeredFormData?.id || null,
      active: registeredFormData?.active || true,
      name: data.name,
      signal_type: data.signalType,
      notice_type: SignalNoticeTypes[1].id,  // 検知系は超過日のみ固定
      categories: [data.category],
      target_days: DETECTION_TARGET_DAYS,
      threshold_level: data.thresholdLevel,
    }});
    setIsLoading(false);
    if (error) {
      setGlobalMessage({
        message: "Signal設定の保存に失敗しました。",
        severity: "error",
      });
      return;
    } else {
      setGlobalMessage({
        message: "Signal設定を保存しました。",
        severity: "success",
      });
      navigate("/signal/");
    }
  };

  return (
    <>
      {isLoading && <FullScreenLoadCircle />}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box sx={{ maxWidth: 600, mx: "auto" }}>
          <FormControl
            fullWidth
            margin="normal"
            error={!!errors.signalType}
          >
            <Controller
              name="signalType"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={signalTypeOptions}
                  value={signalTypeOptions.find(option => option.id === field.value) || null}
                  onChange={(_, newValue) => {
                    field.onChange(newValue?.id || DEFAULT_SIGNAL_TYPE);
                  }}
                  getOptionLabel={(option) => option.label || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={"シグナルタイプ"}
                      placeholder={"選択または検索"}
                    />
                  )}
                />
              )}
            />
            <FormHelperText>
              {errors.signalType?.message}
            </FormHelperText>
          </FormControl>
          <FormControl
            fullWidth
            margin="normal"
            error={!!errors.category}
          >
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={categoryData || []}
                  value={field.value || null}
                  onChange={(_, newValue) => field.onChange(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={"市場(カテゴリー)"}
                      placeholder={"選択または検索"}
                    />
                  )}
                />
              )}
            />
            <FormHelperText>
              {errors.category?.message}
            </FormHelperText>
          </FormControl>
          <FormControl
            fullWidth
            margin="normal"
            error={!!errors.thresholdLevel}
          >
            <Controller
              name="thresholdLevel"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={SignalThresholdLevels}
                  value={SignalThresholdLevels.find(option => option.id === field.value) || null}
                  onChange={(_, newValue) => {
                    field.onChange(newValue?.id || SignalThresholdLevels[0].id);
                  }}
                  getOptionLabel={(option) => option.label || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={"検知感度"}
                      placeholder={"選択または検索"}
                    />
                  )}
                />
              )}
            />
            <FormHelperText>
              {errors.thresholdLevel?.message}
            </FormHelperText>
          </FormControl>
          <FormControl
            fullWidth
            margin="normal"
          >
            <TextField
              {...register("name")}
              variant="outlined"
              fullWidth
              label="設定名"
              error={!!errors.name}
              helperText={errors.name?.message}
            />
          </FormControl>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ mt: 3, mb: 2 }}
            disabled={isSubmitting}
          >
            保存
          </Button>
        </Box>
      </form>
    </>
  )
}
